import React from 'react';
import classNames from 'classnames';
import { TableColumn } from './Table.types';
import TableCell from './TableCell'; // Ensure this import is correct and the file is properly exported in index.ts
// The import appears correct, but the TypeScript error might be due to circular imports
// or the file not being properly built. This can typically be resolved by ensuring
// TableCell.tsx is properly exported in the index file.

interface TableRowProps<T = any> {
  /**
   * Row data object
   */
  data: T;
  
  /**
   * Column definitions
   */
  columns: TableColumn<T>[];
  
  /**
   * Row index in the table
   */
  index: number;
  
  /**
   * Whether the row is in a striped table
   */
  striped?: boolean;
  
  /**
   * Whether the row is hoverable
   */
  hoverable?: boolean;
  
  /**
   * Click handler for the row
   */
  onClick?: (row: T) => void;
  
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * TableRow component that renders a table row with cells based on column definitions.
 */
export const TableRow = <T extends {}>({
  data,
  columns,
  index,
  striped = false,
  hoverable = false,
  onClick,
  className,
}: TableRowProps<T>) => {
  const handleRowClick = () => {
    if (onClick) {
      onClick(data);
    }
  };
  
  return (
    <tr 
      className={classNames(
        "border-t border-gray-200/80 dark:border-white/10 transition-colors",
        {
          'bg-gray-50/80 dark:bg-slate-800/50': striped && index % 2 === 1,
          'hover:bg-primary-50/60 dark:hover:bg-slate-700/55 cursor-pointer': hoverable || !!onClick,
        },
        className
      )}
      onClick={handleRowClick}
    >
      {columns.map((column) => {
        // Apply responsive classes based on priority
        const responsiveClasses = {
          'hidden md:table-cell': column.priority === 'medium',
          'hidden lg:table-cell': column.priority === 'low',
        };
        
        // Get field value
        const fieldName = column.field || column.id;
        const value = fieldName.split('.').reduce((obj, key) => obj?.[key], data as any);
        
        // Format value if formatter is provided
        const formattedValue = column.format ? column.format(value, data) : value;
        
        // Apply alignment classes
        const alignmentClasses = {
          'text-left': column.align === 'left' || !column.align,
          'text-center': column.align === 'center',
          'text-right': column.align === 'right',
        };
        
        return (
          <TableCell
            key={column.id}
            value={formattedValue}
            type={column.type || 'text'}
            className={classNames(responsiveClasses, alignmentClasses, column.className)}
          />
        );
      })}
    </tr>
  );
};

export default TableRow;